import React, { useCallback, useEffect, useState } from 'react';
import { Paperclip, Download, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';

export const RmaAttachments = ({ api, ticketId }) => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api().get(`/doc-evidence?doc_type=warranty_ticket&doc_id=${ticketId}`); setRows(r.data || []); }
    catch { /* non-blocking */ }
  }, [api, ticketId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append('file', f);
        await api().post(`/warranty-tickets/${ticketId}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(`${files.length} file(s) attached`); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); e.target.value = ''; }
  };

  const download = async (a) => {
    try {
      const r = await api().get(`/doc-evidence/${a.id}/file`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: a.content_type }));
      const link = document.createElement('a'); link.href = url; link.download = a.original_filename;
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.original_filename}?`)) return;
    try { await api().delete(`/doc-evidence/${a.id}`); load(); } catch { toast.error('Remove failed'); }
  };

  return (
    <div className="border-t border-slate-100 pt-3" data-testid="rma-attachments">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase text-slate-500 flex items-center gap-1"><Paperclip className="w-3 h-3" /> Attachments ({rows.length})</span>
        <label className="inline-flex items-center gap-1 text-xs px-2.5 py-1 border border-slate-300 rounded-sm cursor-pointer hover:bg-slate-50" data-testid="rma-attach-btn">
          <input type="file" multiple className="hidden" onChange={upload} disabled={busy} accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx,.xls,.xlsx,.csv,.txt" data-testid="rma-attach-input" />
          <Upload className="w-3 h-3" /> {busy ? 'Uploading…' : 'Attach file'}
        </label>
      </div>
      {rows.map(a => (
        <div key={a.id} className="flex items-center justify-between text-sm py-1 border-b border-slate-50 last:border-0" data-testid={`rma-attachment-${a.id}`}>
          <span className="truncate">
            {a.original_filename}
            {a.evidence_type === 'external_rma' && <span className="ml-2 text-[10px] uppercase bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded-sm">External RMA doc</span>}
          </span>
          <span className="flex gap-1 shrink-0">
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => download(a)} data-testid={`rma-attachment-download-${a.id}`}><Download className="w-3.5 h-3.5" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => remove(a)} data-testid={`rma-attachment-delete-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
          </span>
        </div>
      ))}
    </div>
  );
};

export default RmaAttachments;
